import {
  BusinessStoryCandidate,
  CommunityStoryContext,
  VideoFormat,
  ReelFormatPromptTemplateMap,
  ReelFormatPromptTemplateOverrides,
  ThreeStepValidatorPromptTemplateKey,
  ThreeStepValidatorPromptTemplateMap,
  ThreeStepValidatorPromptTemplateOverrides,
} from "./ViralReelCreator.types";
import { UGC_STYLE_PREFIX, PORTRAIT_HINT, GPT_PORTRAIT_SIZE } from "./SlideGenerator.constants";
import {
  createOpenAIChatCompletion,
  createOpenAIImageGeneration,
  createOpenAIImageEdit,
} from "../../../../../../services/openaiFunctions";

export const SEGMENT_COUNT = 12; // tuned for short-form reels
const NARRATION_MIN_SECONDS = 3;
const NARRATION_MAX_SECONDS = 6;
const IMAGE_GENERATION_TIMEOUT_MS = 240000;
const DEFAULT_TARGET_MAX_SCRIPT_WORDS = 14;
const HISTORIC_ARC_TARGET_MAX_SCRIPT_WORDS = 18;
const EXPERT_REVELATION_TARGET_MAX_SCRIPT_WORDS = 24;
const DEFAULT_HARD_MAX_SCRIPT_WORDS = 28;
const HISTORIC_ARC_HARD_MAX_SCRIPT_WORDS = 34;
const EXPERT_REVELATION_HARD_MAX_SCRIPT_WORDS = 42;

type ScriptWordGuidance = {
  targetMaxWords: number;
  hardMaxWords: number;
};

type BusinessStoryScoreKey = "hook" | "pain" | "move" | "reframe" | "lesson";
export type ReelGenerationProgressUpdate = {
  stepId: string;
  message: string;
  percent: number;
};

const BUSINESS_STORY_SCORE_KEYS: BusinessStoryScoreKey[] = ["hook", "pain", "move", "reframe", "lesson"];
const BUSINESS_STORY_SEGMENT_COUNT = 12;
const BUSINESS_STORY_CANDIDATE_COUNT = 5;
const THREE_STEP_VALIDATOR_FORMATS: VideoFormat[] = ["storytelling", "historic-arc"];

export const supportsThreeStepValidator = (format: VideoFormat): boolean =>
  THREE_STEP_VALIDATOR_FORMATS.includes(format);

const getScriptWordGuidance = (format: VideoFormat): ScriptWordGuidance => {
  if (format === "historic-arc") {
    return {
      targetMaxWords: HISTORIC_ARC_TARGET_MAX_SCRIPT_WORDS,
      hardMaxWords: HISTORIC_ARC_HARD_MAX_SCRIPT_WORDS,
    };
  }
  if (format === "expert-revelation") {
    return {
      targetMaxWords: EXPERT_REVELATION_TARGET_MAX_SCRIPT_WORDS,
      hardMaxWords: EXPERT_REVELATION_HARD_MAX_SCRIPT_WORDS,
    };
  }
  return {
    targetMaxWords: DEFAULT_TARGET_MAX_SCRIPT_WORDS,
    hardMaxWords: DEFAULT_HARD_MAX_SCRIPT_WORDS,
  };
};

const VOICE_RULES = `Voice & tone rules:
- First person always ("I", "me", "my") — conversational but confident
- Simple, human language — no hype, no buzzwords, no corporate speak
- Experience-driven: mistakes turned into insights, mindset shifts, practical action
- Each segment is one punchy, complete thought. Keep most lines around {{SCRIPT_WORD_RANGE}} words. Shorter or longer is allowed when needed. Keep each line under {{SCRIPT_WORD_HARD_CAP}} words and usually within ${NARRATION_MIN_SECONDS}-${NARRATION_MAX_SECONDS} seconds when spoken naturally.
- Every segment must flow naturally into the next
- Exception for "historic-arc": third-person narration is required. Preserve any provided creator/community tone, but keep the narration in third person.`;

const AUDIENCE_RULES = `Audience adaptation rules:
- Infer the target audience from the lesson content itself (topic, vocabulary, pain points, outcomes).
- Do NOT default to creators/entrepreneurs unless the source lesson is clearly about creator/business growth.
- Make examples, stakes, and CTA language fit the inferred audience and community context.
- Keep terminology domain-appropriate (e.g., wellness, parenting, faith, fitness, education, sales, leadership, etc.).`;

const HOOK_RULES = `Hook rules:
- Segment 1 is the most important line in the reel. Treat it as the scroll-stopper.
- The first 1-3 seconds must create immediate curiosity, surprise, tension, or a concrete high-stakes consequence.
- Lead with the outcome, contradiction, loss, risk, reversal, taboo, or hidden truth before background.
- Make the premise understandable immediately. Curiosity is good; confusion is not.
- Avoid generic warm-ups like "here's the story", "let me explain", "today we're talking about", or soft scene-setting.
- Use specifics when possible: names, numbers, stakes, consequences.
- Segment 2 should reward the hook by clarifying or escalating it, not restarting the setup.
- If segment 1 already clearly names the protagonist, company, or event, segment 2 must deepen the conflict instead of reintroducing who it is.`;

export const HISTORIC_ARC_EXAMPLES = `STYLE REFERENCE FOR HISTORIC-ARC:
- Open on a shocking consequence, contradiction, or reversal.
- Keep every line short, visual, and easy to follow on first listen.
- Escalate with new information every 1-2 segments.
- Use concrete names, places, dates, stakes, and outcomes.
- End on a practical lesson earned by the story, not vague inspiration.
- Match the pacing and tension only. Do not reuse any example subject matter.
- CRITICAL: ALL 12 segments stay in third-person narrator voice — including the lesson at the end. Never switch to first person. The lesson is delivered as a universal observation drawn from the story ("The companies that survived…", "What the story reveals…"), never as personal reflection ("I learned…", "I used to…").`;

const CHARACTER_CONSISTENCY_RULES = `Character consistency rules for image prompts:
- Define ONE recurring main character. They do NOT need to appear in every segment — only include them when it makes visual sense (e.g. skip for abstract, environmental, or concept-only scenes).
- Lock identity traits: approximate age, skin tone, facial structure, hairstyle, outfit style/colors, and key accessory.
- Do not swap protagonists, gender expression, ethnicity, or wardrobe style between segments.
- When the character appears, the imagePrompt MUST start with: "CHARACTER_ANCHOR: <same descriptor sentence>." using the exact same descriptor each time.
- When the character does NOT appear, omit the CHARACTER_ANCHOR prefix entirely.`;

const VISUAL_CONSISTENCY_RULES = `Visual consistency rules for image prompts:
- Lock ONE shared visual direction across the entire reel.
- Keep the same medium, realism level, lens feel, lighting style, color palette, texture, and mood across all segments.
- Do not switch visual treatment between photo, illustration, render, animation, collage, painting, or graphic design.
- Do NOT include any "Shared visual style:" text — the visual style is added automatically.
- The scene content can change by segment, but the visual language must stay recognizably the same from start to finish.`;

export const DEFAULT_CHARACTER_ANCHOR =
  "same main character, consistent face, age range, hairstyle, outfit palette, and key accessory across all segments";
const DEFAULT_VISUAL_STYLE_ANCHOR =
  "cinematic realistic photography, cohesive color palette, consistent lighting direction, natural depth of field, subtle texture, and the same overall mood across the reel";

const CHARACTER_ANCHOR_PREFIX = "CHARACTER_ANCHOR:";
const VISUAL_STYLE_PREFIX = "Shared visual style:";
const VISUAL_STYLE_SUBJECT_PATTERNS = [
  /\bcharacter\b/i,
  /\bprotagonist\b/i,
  /\bman\b/i,
  /\bwoman\b/i,
  /\bfemale\b/i,
  /\bmale\b/i,
  /\bentrepreneur\b/i,
  /\bcreator moments?\b/i,
  /\bscenes show\b/i,
  /\bwears\b/i,
  /\bhair\b/i,
  /\bmakeup\b/i,
  /\bexpression\b/i,
  /\bface\b/i,
  /\boutfit\b/i,
  /\baccessor(?:y|ies)\b/i,
  /\b\d{1,2}\s*year\s*old\b/i,
  /\bworking on\b/i,
  /\bwriting ideas\b/i,
  /\bwalking while\b/i,
  /\bchecking analytics\b/i,
  /\brecording content\b/i,
  /\bplanning products\b/i,
];

export const extractCharacterAnchor = (prompt: string): string | null => {
  if (!prompt) return null;
  const match = prompt.match(/CHARACTER_ANCHOR:\s*([^\n]+?)(?:\.\s|$)/i);
  return match?.[1]?.trim() || null;
};

const stripCharacterAnchor = (prompt: string): string =>
  (prompt || "").replace(/^\s*CHARACTER_ANCHOR:\s*[^\n]+?(?:\.\s*|$)/i, "").trim();

export const applyCharacterAnchor = (prompt: string, anchor?: string): string => {
  const safeAnchor = (anchor || DEFAULT_CHARACTER_ANCHOR).trim();
  const body = stripCharacterAnchor(prompt || "");
  return body
    ? `${CHARACTER_ANCHOR_PREFIX} ${safeAnchor}. ${body}`
    : `${CHARACTER_ANCHOR_PREFIX} ${safeAnchor}.`;
};

const stripSharedVisualStyle = (prompt: string, knownAnchor?: string): string => {
  let result = prompt || "";
  if (knownAnchor) {
    const esc = knownAnchor.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    result = result.replace(new RegExp(`\\s*Shared visual style:\\s*${esc}\\.?\\s*`, "gi"), " ");
  }
  // Greedy fallback: strip any remaining "Shared visual style: ..." block (spans multiple sentences)
  result = result.replace(/\s*Shared visual style:\s*.+/gi, "");
  return result.replace(/\s{2,}/g, " ").trim();
};

const sanitizeVisualStyleAnchor = (visualStyle?: string): string => {
  const clean = ((visualStyle || "").trim() || DEFAULT_VISUAL_STYLE_ANCHOR).replace(/\*\*/g, "").replace(/\s+/g, " ");
  const sentences = clean
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.replace(/\s+/g, " ").trim())
    .map((sentence) => sentence.replace(/\s+featuring\b.*$/i, "").trim())
    .filter(Boolean)
    .filter((sentence) => !VISUAL_STYLE_SUBJECT_PATTERNS.some((pattern) => pattern.test(sentence)));

  const deduped = Array.from(new Set(sentences));
  const result = deduped.join(" ").replace(/\s+/g, " ").trim().replace(/\.*$/, "");
  return result || DEFAULT_VISUAL_STYLE_ANCHOR;
};

const normalizeVisualStyleAnchor = (visualStyle?: string): string => sanitizeVisualStyleAnchor(visualStyle);

const applyVisualStyleAnchor = (prompt: string, visualStyle?: string): string => {
  const styleAnchor = normalizeVisualStyleAnchor(visualStyle);
  const characterAnchor = extractCharacterAnchor(prompt);
  const bodyWithoutCharacter = stripCharacterAnchor(prompt || "");
  const body = stripSharedVisualStyle(bodyWithoutCharacter, styleAnchor);

  const stylePrefix = `${VISUAL_STYLE_PREFIX} ${styleAnchor}.`;
  const characterSuffix = characterAnchor ? ` ${CHARACTER_ANCHOR_PREFIX} ${characterAnchor}.` : "";

  return body
    ? `${stylePrefix} ${body}${characterSuffix}`
    : `${stylePrefix}${characterSuffix}`;
};

const buildCommunityContextNote = (communityContext?: CommunityStoryContext): string => {
  if (!communityContext) return "";

  const lines = [
    communityContext.title ? `- Community title: ${communityContext.title}` : "",
    communityContext.categoryName ? `- Category: ${communityContext.categoryName}` : "",
    communityContext.shortDescription
      ? `- Short description: ${communityContext.shortDescription}`
      : "",
    communityContext.description ? `- Description: ${communityContext.description}` : "",
    communityContext.tags?.length ? `- Tags: ${communityContext.tags.join(", ")}` : "",
  ].filter(Boolean);

  const buyerPersonaNote = communityContext.buyerPersona?.trim()
    ? `\nTarget audience (buyer persona): ${communityContext.buyerPersona.trim()}. Write every hook as if you are speaking directly to this specific person — their fears, frustrations, and desires. The hook should make them feel like this reel was made just for them.`
    : "";

  return lines.length ? `Community context:\n${lines.join("\n")}${buyerPersonaNote}` : buyerPersonaNote.trim() ? `Community context:${buyerPersonaNote}` : "";
};

export const DEFAULT_REEL_FORMAT_PROMPTS: Record<VideoFormat, string> = {
  "hard-truth": `Follow this ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (Scroll-Stopping Hook): ONE powerful contrarian truth or painful admission. Must make someone STOP scrolling. Use: "I [did wrong thing] for [X time]" OR "Everyone told me to [X]. They were wrong." OR "Working harder was why I was failing."
- Segments 3-4 (What Most People Do Wrong): Name the specific common mistake — create contrast.
- Segments 5-6 (The Real Problem): Reveal the deeper reason it doesn't work — what most people miss.
- Segments 7-9 (The Evidence): Real examples, numbers, or undeniable logic that proves the hard truth.
- Segments 10-12 (The Shift): The mindset or system change that actually works. Be specific.
- Segment 13 (The Proof): Back it with a real outcome or result.
- Segments 14-15 (Subtle CTA): Low-pressure next step. Natural, not salesy.`,

  storytelling: `Follow this STORY ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (Hook Scene): Drop into a specific vivid moment mid-story. Create immediate curiosity — "I was sitting in my car when my phone lit up..." Start in action.
- Segments 3-4 (Character/Context): Who you were then, why this moment mattered, what was at stake.
- Segments 5-6 (The Conflict): The tension builds. Something goes wrong or challenges you. Be specific.
- Segments 7-8 (Turning Point): The exact moment everything shifted — a decision, a conversation, a realization.
- Segments 9-11 (Rising Action): What you did differently. Steps taken, small wins, the path forward.
- Segments 12-13 (Resolution): Where it led. What changed. The concrete result.
- Segments 14-15 (The Lesson + CTA): The broader truth this story reveals and where to go next.`,

  tutorial: `Follow this TUTORIAL ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (Result-First Hook): Open with the outcome you achieved — specific numbers or transformation. "I [did X] in [time]. Here's exactly how."
- Segments 3-4 (Why Most People Fail at This): Set up the contrast — the wrong approach vs yours.
- Segments 5-6 (Step 1 — Foundation): The first concrete action. Not mindset — a real move. Common pitfall at this step.
- Segments 7-9 (Step 2 — The Build): What comes next. The thing most people skip or get wrong here.
- Segments 10-11 (Step 3 — The Lever): The highest-impact move. The non-obvious insight that changed everything.
- Segments 12-13 (The Result + What to Skip): The outcome and the biggest time-wasting mistake to avoid.
- Segments 14-15 (CTA): Where to go deeper. Natural mention of next step.`,

  "before-after": `Follow this TRANSFORMATION ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (Hook — The Before): Open with the old version of yourself in a vivid, specific, relatable moment. Make it feel real and slightly painful.
- Segments 3-4 (What It Was Costing): The specific price you paid — time, money, freedom, identity.
- Segments 5-6 (The Breaking Point): The exact moment or realization that forced the change.
- Segments 7-8 (The Discovery): What you found, learned, or tried differently. The specific thing that opened the door.
- Segments 9-10 (The Switch): What you did differently — the specific system, mindset, or decision.
- Segments 11-12 (The After): What life looks like now — specific, tangible, earned. Not hype.
- Segment 13 (The Contrast): Side-by-side of before vs after to drive it home.
- Segments 14-15 (CTA): Point to the next step. Natural, not forced.`,

  "day-in-life": `Follow this DAY-IN-THE-LIFE ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (Hook): Start with the most interesting, surprising, or unconventional moment of your day. Make them curious about your lifestyle.
- Segments 3-4 (Morning/Setup): Morning ritual or work setup. What you do first. What most people do instead.
- Segments 5-6 (First Challenge): Something real went sideways or was harder than expected. Authentic, not polished.
- Segments 7-9 (Focus Time): Deep work session. What you're building. What tools, systems, routines make it possible.
- Segments 10-11 (The Win): A small or big breakthrough. A client, result, or moment worth sharing.
- Segments 12-13 (Reflection): What you learned or noticed today. One insight worth remembering.
- Segments 14-15 (CTA): Invite them into your world in a way that fits the audience (community, next step, resource, or next video).`,

  "ugc-rant": `Follow this UGC RANT ARC across {{SEGMENT_COUNT}} segments:
- Segments 1-2 (The Controversial Take): Open with a strong, polarizing opinion. Something that makes people say "wait, what?" — but is clearly backed by experience.
- Segments 3-4 (Who This Is For): Clarify who you're talking to and why this matters to them specifically.
- Segments 5-6 (The Problem with the Status Quo): What most people do / believe / are told — and why it's keeping them stuck.
- Segments 7-8 (Real Evidence): Specific examples, receipts, numbers, or stories that back your take.
- Segments 9-10 (The Counter-Argument): Acknowledge the pushback and dismantle it with logic and experience.
- Segments 11-13 (What to Do Instead): The real, practical alternative. Actionable and direct.
- Segments 14-15 (CTA + Mic Drop): End on a memorable line that crystallizes your point. Then the ask.`,

  "expert-revelation": `You are writing a viral expert authority reel. The speaker is a practitioner from an ancient, alternative, or suppressed tradition who has seen this exact problem across thousands of real people. They speak with calm certainty, not hype.
{{CHARACTER_IDENTITY}}
VOICE: The speaker addresses the VIEWER directly. Most lines use "you" and "your" — the expert is diagnosing, revealing, and instructing the person watching. First person ("I", "my") is only used when the speaker shares what they have personally seen, done, or built — never to describe the viewer's problem as if it belongs to the speaker.

The community context determines the tradition, the enemy, and the domain. A health community gets a Chinese medicine practitioner or herbalist. A financial community gets a generational wealth builder who watched the system fail people. A spiritual community gets a lineage teacher. A business community gets a practitioner who built before there were courses. Do not default to any tradition unless the community context points there.

{{COMMUNITY_GROUNDING_LINE}}Follow this EXACT ARC across 11 segments:

SEGMENT 1 — THE SYMPTOM HOOK:
Address the VIEWER directly — this is about their problem, not the speaker's. The speaker is the expert observing the viewer's situation from the outside. Use "you", "your", "if you", not "I" or "my".
Three valid structures:
- Reframe hook: "If you are still [struggling with X], it is not your [common excuse 1] or your [common excuse 2]." — give two things the viewer has already blamed, then stop. Do NOT reveal the real cause yet. Leave it open. The payoff comes later. Example: "If you are still not able to sell online, it is not your product or your price." Full stop. Let them wonder what it actually is.
- Visual mirror: "If your [observable thing in their world] looks like [this], that is not [what they think]. That is [what it really is]."
- Consequence mirror: "If you have been [doing common thing] and still [not getting result], [specific reason why]."
The viewer must recognize themselves immediately. The speaker is diagnosing them, not confessing. Never open with "I". Never open with "my". Never open with a question. Never open with "have you ever."

SEGMENT 2 — THE AUTHORITY ANCHOR:
Name a specific, real tradition by its actual name — derived from the community context above. State it has known this answer for a long time. Plain fact, not boast.

"In [named tradition], we have known the root of this for [time period]."
Or: "We have never seen [surface symptom] as the problem. We see it as where the [body / system / person] pushes what it cannot clear."

TIME PERIOD RULES — match the actual age of the tradition:
- Ancient traditions (TCM, Ayurveda, herbalism, stoicism, lineage practices): "thousands of years" or "centuries"
- Established modern fields (psychology, nutrition science, physical therapy, strength training): "decades"
- Newer disciplines (direct response marketing, growth hacking, digital marketing, SaaS): "decades" — NEVER "centuries" or "generations"
- NEVER use "generations" — it is vague and feels lazy. Pick a real timeframe.

Examples:
- "In Chinese medicine, we have known the root of this for thousands of years."
- "In Ayurvedic practice, we have never seen the skin as the problem. We see it as where the body pushes what the gut cannot clear."
- "In strength and conditioning, we have known this for decades."
- "In direct response marketing, we have known this for decades."

FORBIDDEN: "in my world", "in our community", "we learned this", "generations", "years ago", any unnamed tradition.

SEGMENT 3 — THE ENEMY:
Name the mainstream authority in this domain and the specific bad solution they offer. Be precise, not vague.
Structure: "[Mainstream authority] [charges you / has one answer / will just tell you] [specific bad or expensive solution]. [One-line dismissal of why it doesn't work or who it benefits]."
The enemy must profit from not solving the root. That is the implicit accusation in every line.

SEGMENT 4 — THE PATIENT MIRROR:
Name what it actually feels like to live with this problem. Use the people who came to you as the vehicle.
"Most people who come to me with this [behavioral consequence showing emotional cost]."
Then rapid-fire: two to three more lines showing what they have already tried and failed.
"They have tried [mainstream solution]. [Mainstream solution]. Nothing worked."
Each line is a new hook. Someone watching will recognize themselves in at least one.

SEGMENT 5 — THE COMMAND PIVOT:
Two words. Exact words. Do not vary them:
"Listen to me."
Then immediately: "Here is exactly what you do." or "Here is what removes this." or "Here is what we use."
This is a compliance trigger. It signals that the payoff is coming. It stops the scroll.

SEGMENT 6 — THE PROTOCOL:
The specific remedy. Numbered or sequential. Accessible ingredients or steps — things already in the kitchen, the body, or the daily routine.
Each step gets exactly one sentence of justification immediately after it.
"[Step or ingredient]. [One sentence explaining what it does at the mechanism level]."
Never a bare list. Every item earns its place with a reason.

SEGMENT 7 — THE LEGITIMACY ANCHOR:
One line that roots the protocol in the specific tradition named in segment 2. Do not repeat the phrase "this is ancient knowledge, not a new discovery" — derive the legitimacy from the actual tradition and timeframe used earlier.
Structure: "This is the same [protocol / remedy / practice / system] [tradition] has used for [timeframe]." or "I have given this exact [protocol] to every person who walks through my door."
FORBIDDEN: "this is ancient knowledge, not a new discovery" verbatim, any generic legitimacy phrase that does not reference the specific tradition from segment 2.

SEGMENT 8 — THE LOOP REOPENER:
Just as the viewer feels satisfied, open a new question. Use one of these exact transition phrases:
"Now here is what nobody tells you."
"But here is the part most people miss."
"But they keep coming back. And we always ask why."
Then immediately ask the root cause question: "Why did it happen in the first place?" or "Why does it keep returning?"
Do not answer yet. The question is the beat.

SEGMENT 9 — THE HIDDEN CONNECTION:
Reveal the deeper systemic cause — the layer the mainstream never addresses and the tradition has always known. Make the connection specific to this community's domain.
Structure: "In [tradition], [surface symptom] and [root system] are more connected than you think."
Then: "What [root system] cannot clear, [surface] carries." or "Until [root] is addressed, [surface] keeps collecting what it cannot process."

Examples of the specificity required:
- Health: "In Chinese medicine, the mouth and the gut are more connected than you think. What the gut cannot clear, the mouth carries."
- Business: "In direct response, your offer and your audience's awareness are more connected than you think. What the market cannot trust, no price will move."
- Spiritual: "In Stoic practice, your outer chaos and your inner judgments are more connected than you think. What the mind cannot name, the life reflects."

SEGMENT 10 — THE SUBTLE APP CTA:
One to two lines maximum. Never a hard sell. The app is the natural next step for someone who wants to go deeper — not a pitch interrupting the value.

Structure:
"If you want [the full version of what was just revealed / the complete system / to go deeper on this], [brandName] is where I teach it."
Or: "Everything I just showed you is the surface. The full practice lives inside [brandName]."
Or: "I built [brandName] for exactly this — so you never have to figure this out alone."

FORBIDDEN: "download", "buy", "sign up", "check out". The app name drops naturally, like a place the viewer is being invited into — not sold to. No exclamation marks. No urgency language. No limited-time framing.

SEGMENT 11 — THE IDENTITY CLOSE:
Three lines. Exact structure. Do not compress into one:
Line 1: "I am [Name]." — the persona's name, stated plainly.
Line 2: "I [verb] [domain-specific thing] with one goal." — what they do, in the language of this community's world.
Line 3: "To [audacious, specific, mission with a number or scale]." — the mission must feel like a movement, not a tagline. A number makes it real.

Examples:
- "I am Chen. I share ancient health wisdom with one goal. To transform a hundred million American lives."
- "I am NOVA. I teach digital product creation with one goal. To put a thousand ideas into the world that would have died unbuilt."
- "I am Marcus. I teach generational wealth with one goal. To close the wealth gap for ten million families."

TONE RULES:
- Every sentence is its own line. No compound sentences joined by "and" or "but" mid-thought.
- Plain spoken English. No clinical language unless immediately translated into plain terms.
- The speaker never brags. They state facts about what they have seen and what works.
- Urgency comes from the problem, not from the speaker's tone.
- No exclamation marks. No filler words.
- The protocol must feel suppressed, not experimental — ancient, not trendy.
- The enemy is never attacked emotionally. They are dismissed with one precise factual line.

IMAGE PROMPT RULES FOR THIS FORMAT:
Use the character when the segment naturally works as a direct-to-camera or founder/expert moment. Do not force the character into abstract, environmental, proof, customer, or object-focused segments.
- Default character framing when used: medium shot or close-up, facing the lens, slightly casual and confident
- When the script references something visible (a body part, an object, a result, a product, a texture, a symptom, a movement): either show the character demonstrating it when natural, or show the visible thing in a human-context scene.
- Vary framing naturally: tight on face when the line is personal/revelatory, wider or environment-focused when the line is about evidence, setting, customers, or consequences
- Include the CHARACTER_ANCHOR prefix only when the recurring character appears in that segment
- Do NOT include any "Shared visual style:" text — the visual style is added automatically`,

  "historic-arc": `${HISTORIC_ARC_EXAMPLES}

{{COMMUNITY_GROUNDING_LINE}}Follow this VIRAL REAL STORY ARC across {{SEGMENT_COUNT}} segments:
GOAL: Tell one specific, high-intrigue real story that naturally reveals the lesson.

VOICE:
- Third-person narration only for ALL 12 segments — including the closing lesson.
- Never use "I", "me", "my", or "we" at any point in any segment.
- The lesson segment (12) must be a universal observation drawn from the story (e.g. "The companies that outlasted them…", "What this teaches any builder…"), not a personal reflection.
- Match the community tone, but keep the language punchy, spoken, concrete, and easy to follow.
- Every segment must add new information.

STORY RULES:
- Base the reel on ONE identifiable real story, not a vague archetype.
- Prefer a story from the community's own world when possible.
- If a recent real story fits the lesson better than older history, use the recent story.
- Pick a story with built-in stakes, conflict, contradiction, collapse, reversal, breakthrough, betrayal, obsession, or consequence.
- Avoid bland operational setups, generic case studies, and default printing-press stories unless the lesson is actually about publishing or media distribution.

ANCHOR RULES:
- The viewer should know what story this is within the first 1-2 segments.
- Name the real protagonist, company, institution, movement, or event as early as possible.
- Do not rely on anonymous hooks like "her business" unless the next line immediately identifies the story.
- Do not waste segment 2 on "this was X" or other identity restatements if segment 1 already made the identity clear.

LESSON FIT:
- Reduce the lesson to its core principle first.
- Then choose a real story driven by that same principle.
- Different lessons need materially different source stories.

DELIVERY:
- This is a social reel, not a documentary script or textbook.
- Use plain modern spoken English and avoid overloaded date-name-location lines.
- Avoid soft scene-setting openings unless the surprising consequence lands immediately.
- Be specific with names, places, dates, and numbers when they improve clarity.
- Do not invent dramatic facts. Plausible specificity beats fake certainty.

ARC STRUCTURE:
- Segment 1: shocking outcome, contradiction, or reversal, ideally with the real protagonist or event in the line.
- Segment 2: if identity is still unclear, identify who or what this story is about; otherwise escalate the conflict, stakes, or surprising context immediately.
- Segments 3-4: setup and tension.
- Segments 5-6: escalation and rising stakes.
- Segments 7-8: turning point, decision, or tactic.
- Segments 9-10: reversal and visible consequences.
- Segment 11: outcome or aftermath.
- Segment 12: the practical lesson revealed by the story.`,
};

export const getDefaultReelFormatPrompts = (): ReelFormatPromptTemplateMap => ({
  ...DEFAULT_REEL_FORMAT_PROMPTS,
});

export const getReelFormatPrompt = (
  format: VideoFormat,
  reelFormatPromptTemplates?: ReelFormatPromptTemplateOverrides
): string => {
  const override = reelFormatPromptTemplates?.[format];
  return typeof override === "string" && override.trim()
    ? override
    : DEFAULT_REEL_FORMAT_PROMPTS[format] || DEFAULT_REEL_FORMAT_PROMPTS["hard-truth"];
};

const stripImagePromptRulesFromArc = (template: string): string =>
  (template || "").replace(/\n*IMAGE PROMPT RULES FOR THIS FORMAT:[\s\S]*$/i, "").trim();

const replacePromptVariables = (template: string, variables: Record<string, string>): string =>
  Object.entries(variables).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{\\{${key}\\}\\}`, "g"), value),
    template
  );

export const DEFAULT_THREE_STEP_VALIDATOR_PROMPTS: ThreeStepValidatorPromptTemplateMap = {
  storySelection: `You are selecting the best possible story for a viral short-form reel.

Your job is NOT to write the script yet.
Your job is to find the strongest real story candidates from the community's domain.

Find {{CANDIDATE_COUNT}} candidate stories.

For each candidate, return:
1. Founder or company name
2. Specific year or time period
3. The painful problem
4. The one decisive move that changed everything
5. A measurable result with a specific number
6. Why this story has strong visual potential for short-form video or claymation
7. What the likely reframe or punchline could be
8. A score from 1 to 10 for how strong this story is for a viral reel

What makes a strong candidate:
- The problem is painful, specific, and easy to visualize
- The founder was wrong, stuck, ignored, rejected, or close to failure
- One move clearly changed the trajectory
- The result is measurable with a hard number
- The lesson is useful for this community's audience today
- The likely reframe feels sharp and non-obvious
- Every candidate is materially different from the others. Do not repeat the same founder, company, event, or case study twice.

Avoid:
- generic startup stories with weak pain
- stories where the lesson is obvious from the start
- stories without numbers
- stories that rely on hype instead of contrast
- overused examples unless the decisive move is unusually sharp

Prefer:
- positioning shifts
- packaging shifts
- pricing shifts
- audience shifts
- onboarding shifts
- distribution shifts
- demand validation stories
- cases where trust or attention turned into measurable demand

Return your answer in valid JSON only using this format:
[
  {
    "name": "string",
    "year": "string",
    "problem": "string",
    "decisive_move": "string",
    "result": "string",
    "visual_potential": "string",
    "likely_reframe": "string",
    "score": 0
  }
]`,
  scriptGeneration: `Use this reel type prompt template as the source-of-truth structure for the draft:
{{REEL_TYPE_STRUCTURE_GUIDANCE}}

Do not add extra structure beyond what the reel type template defines.`,
  scoring: `You are a brutal short-form video script editor who has studied hundreds of viral reels.
You know exactly what makes a viewer stop scrolling versus keep scrolling.
You score with no mercy and no politeness.
Your only reference point is what actually performs — not what sounds smart.

BENCHMARK SCRIPTS (these are real scripts that exceeded 900K views — use them as your scoring anchor):

BENCHMARK 1 (1.4M views — Michelin):
"One day in 1900, the Michelin brothers had a problem.
They made tires in France, but hardly anyone owned cars.
There were only about 3,000 cars in all of France.
André and Edouard Michelin needed people to drive more.
More driving means more worn tires, which means more sales.
So they had an idea.
Make people want to take road trips.
They created the Michelin Guide, a free booklet for drivers.
It listed hotels, gas stations, and mechanics across France.
Also included restaurants worth visiting.
The goal: give people reasons to drive long distances.
At first, it was just practical information.
Then in 1926, they started rating restaurants.
One star meant worth stopping for.
Two stars meant worth a detour.
Three stars meant worth a special journey.
The stars became prestigious.
Chefs obsessed over them.
Losing a star could ruin a restaurant.
Gaining one meant fame.
Michelin hired anonymous inspectors to visit restaurants.
Made it all mysterious and authoritative.
The guide became more famous than the tires.
But it worked.
People drove all over France chasing starred restaurants.
Wearing out their tires.
Buying new Michelin ones.
Today, Michelin stars are the most prestigious culinary award in the world.
Top chefs have cried, quit, and even died over them.
All because a tire company wanted to sell more rubber.
The lesson? Create demand for your product indirectly.
Michelin didn't advertise tires. They created reasons to use them."

BENCHMARK 2 (988K views — Costco):
"One day in 1985, Costco set the price for their hot dog and soda combo.
$1.50. That was almost 40 years ago.
Today, the price is still $1.50.
But along the way, inflation happened.
Costs went up. Price stayed frozen.
In 2009, the CFO approached CEO Jim Sinigal with a suggestion.
We need to raise the hot dog price. We're losing money on every single one.
Sinigal looked at him and said: if you raise the price of the hot dog, I will kill you.
He wasn't joking.
The CFO never brought it up again.
Today, each hot dog costs Costco about $2 to make and sell.
They sell it for $1.50, losing $0.50 on every hot dog sold.
They sell over 100 million hot dogs per year.
That's $50 million in losses annually, on purpose.
So why does Costco do this?
Because the hot dog isn't the product. You are.
The hot dogs are placed at the back of the store.
You walk past everything to get there.
Impulse buys everywhere.
And the $1.50 price is so famous, it gets people in the door.
You come for the cheap hot dog.
Leave with $200 in groceries, electronics, and random stuff you didn't plan to buy.
Costco even built their own hot dog manufacturing plants just to keep control of costs.
Most companies would have raised it years ago.
But Costco understands something others don't.
The hot dog is marketing, not food.
It's a loss leader.
The bait that gets you into the store.
The lesson? Strategic losses create overall wins.
Don't think about profit per item. Think about profit per customer."

SCORING DIMENSIONS — anchor every score against the benchmarks above:

hook (segment 1):
- 9-10: Immediate tension, specific year and name, implies something already going wrong. "One day in 1985, Costco set the price for their hot dog and soda combo. $1.50." The viewer is curious before the second sentence.
- 6-8: Opens on the story but needs a beat or two to create tension. Correct but slow.
- below 6: Generic setup, missing year or name, no implied pressure. "In 2013, a founder launched a product."

pain (segments 2-4):
- 9-10: A specific number that stings. A real consequence that feels immediate. "There were only about 3,000 cars in all of France." / "We're losing money on every single one."
- 6-8: Has a number but the stakes feel manageable or polite.
- below 6: No number. Vague difficulty. "Things weren't going well." / "The launch underperformed."

move (segment 4-6):
- 9-10: One sentence that drops the move without explanation. Sounds almost too simple. "So they gave away a book." / "So he froze the price at $1.50 forever."
- 6-8: States the move but with too much justification or setup around it.
- below 6: Never actually names the move. "So he made one hard move." Full stop.

reframe (segment 7):
- 9-10: One sentence that completely flips the story. Physical and concrete. No abstract subject. "Because the hot dog isn't the product. You are." / "The guide became more famous than the tires. But it worked."
- 6-8: Has a flip but it is expected, explained, or slightly soft. The viewer saw it coming.
- below 6: Explains the lesson instead of delivering the punch. "This shows that positioning matters." / "He understood his audience."

lesson (segments 10-12):
- 9-10: Blunt, practical, directed at the viewer. Earns its authority from the story. "Don't think about profit per item. Think about profit per customer." / "Michelin didn't advertise tires. They created reasons to use them."
- 6-8: Correct advice but sounds like something the viewer has heard before.
- below 6: Generic. Polite. Could appear in a LinkedIn post. "It is important to present your product clearly."

Score the script and return ONLY valid JSON with numeric scores from 0 to 10:
{"hook":0,"pain":0,"move":0,"reframe":0,"lesson":0}`,

  rewrite: `You are rewriting a short-form business story reel to make it hit much harder.

Your job is NOT to change the story.
Your job is to make the writing sharper, more visual, more painful, more memorable, and more brutal.

Keep the same {{SEGMENT_COUNT}}-segment structure.
Keep the same core facts.
Do not invent new facts.
Do not add outside examples.
Do not make it longer unless needed for clarity.
Do not soften anything.

This rewrite should feel less like business education and more like a real story unfolding in front of the viewer.

Rewrite it so that:
- the hook creates immediate tension
- the pain feels specific and humiliating
- the consequence feels real
- the move feels deceptively simple
- the execution feels visible and concrete
- segment 7 feels like a slap in the face
- the lesson feels blunt, useful, and hard to ignore
- the final line feels like a shove

Rules:
- Replace vague words with hard specifics wherever possible
- Cut abstract business language
- Cut consultant tone
- Cut hindsight-heavy phrasing
- Prefer physical, visible, concrete language
- Prefer contrast over explanation
- Prefer pressure, rejection, risk, waste, invisibility, or embarrassment over neutral difficulty
- If a line sounds like a LinkedIn post, rewrite it
- Keep each segment short and spoken-friendly
- Do not remove useful numbers
- Do not turn the lesson into generic advice
- Preserve the strongest line if it already works

First, silently diagnose the weakest parts.
Then rewrite the full script.

Return ONLY the improved valid JSON array of exactly {{SEGMENT_COUNT}} strings.`,
};

export const getDefaultThreeStepValidatorPrompts = (): ThreeStepValidatorPromptTemplateMap => ({
  ...DEFAULT_THREE_STEP_VALIDATOR_PROMPTS,
});

const getThreeStepValidatorPrompt = (
  key: ThreeStepValidatorPromptTemplateKey,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides
): string => {
  const override = threeStepValidatorPromptTemplates?.[key];
  return typeof override === "string" && override.trim()
    ? override
    : DEFAULT_THREE_STEP_VALIDATOR_PROMPTS[key];
};

const buildReelScriptSystemPrompt = (
  format: VideoFormat,
  styleNote: string,
  communityContextNote: string,
  targetMaxScriptWords: number,
  hardMaxScriptWords: number,
  characterDescription?: string,
  reelFormatPromptTemplates?: ReelFormatPromptTemplateOverrides
): string => {
  const voiceRulesWithLengthGuidance = VOICE_RULES
    .replace(/\{\{SCRIPT_WORD_RANGE\}\}/g, `6-${targetMaxScriptWords}`)
    .replace(/\{\{SCRIPT_WORD_HARD_CAP\}\}/g, String(hardMaxScriptWords));
  const base = `You are a viral social media reel writer creating a 60-second script from a lesson. Structure the content into exactly ${SEGMENT_COUNT} segments, aiming for roughly ${NARRATION_MIN_SECONDS}-${NARRATION_MAX_SECONDS} seconds of narration per segment.\n\n${voiceRulesWithLengthGuidance}\n\n${AUDIENCE_RULES}\n\n${HOOK_RULES}\n\n`;
  const communityGroundingLine = communityContextNote
    ? "Use the provided community context to choose the world, stakes, and protagonist.\n\n"
    : "";

  const characterIdentityBlock = characterDescription?.trim()
    ? `\nSPEAKER IDENTITY:\nThe expert in this reel is: ${characterDescription.trim()}. Use this to define their name, background, credentials, tradition, and domain. Every choice — the tradition they invoke, the enemy they name, the protocol they teach — must be consistent with who this person is.\n`
    : "";

  const arcTemplate = getReelFormatPrompt(format, reelFormatPromptTemplates)
    .replace(/\{\{SEGMENT_COUNT\}\}/g, String(SEGMENT_COUNT))
    .replace(/\{\{COMMUNITY_GROUNDING_LINE\}\}/g, communityGroundingLine)
    .replace(/\{\{CHARACTER_IDENTITY\}\}/g, characterIdentityBlock);

  const scriptOnlyArc = stripImagePromptRulesFromArc(arcTemplate);
  const arcWithStyle =
    scriptOnlyArc +
    styleNote +
    `\n\nScript-only mode:
- Ignore any instruction related to image prompts.
- Focus only on writing high-quality narration lines.`;

  return (
    base +
    arcWithStyle +
    `\n\nFor EACH of the ${SEGMENT_COUNT} segments return only the spoken narration line.
Each line should be usually 6-${targetMaxScriptWords} words, but can be shorter or longer when needed. Hard cap ${hardMaxScriptWords} words.
The line must sound natural spoken narration, not a cryptic headline.
For non-historic arcs use first person. For historic-arc: use third-person narrator voice for every segment, including the final lesson.

Return ONLY a JSON array of exactly ${SEGMENT_COUNT} strings: ["segment 1 narration", "segment 2 narration", ...]`
  );
};

const parseJsonArray = (content: string): any[] => {
  const clean = content.trim().replace(/^```json\s*/i, "").replace(/^```\s*/i, "").replace(/\s*```$/i, "");
  try {
    const parsed = JSON.parse(clean);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    const start = clean.indexOf("[");
    const end = clean.lastIndexOf("]");
    if (start !== -1 && end > start) {
      try {
        const parsed = JSON.parse(clean.slice(start, end + 1));
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }
};

const normalizeScriptText = (script: string): string =>
  (script || "").replace(/\s+/g, " ").trim();

const buildFallbackScripts = (text: string): string[] => {
  const sentences = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .flatMap((line) => line.split(/(?<=[.!?])\s+/))
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter(Boolean);

  if (!sentences.length) {
    return Array.from(
      { length: SEGMENT_COUNT },
      (_, idx) =>
        idx === 0
          ? "I learned this the hard way, and it changed everything."
          : "Here is the next piece that made the strategy work in real life."
    );
  }

  return Array.from({ length: SEGMENT_COUNT }, (_, idx) => sentences[idx % sentences.length]);
};

const normalizeGeneratedScripts = (
  parsed: any[],
  fallbackScripts: string[]
): string[] =>
  Array.from({ length: SEGMENT_COUNT }, (_, idx) => {
    const row = parsed[idx] || {};
    const scriptCandidate =
      typeof row === "string" && row.trim()
        ? row.trim()
        : typeof row.script === "string" && row.script.trim()
          ? row.script.trim()
          : fallbackScripts[idx];
    return normalizeScriptText(scriptCandidate);
  });

const parseScore = (value: unknown): number => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(10, numeric));
};

const normalizeBusinessStoryCandidates = (parsed: any[]): BusinessStoryCandidate[] =>
  parsed
    .map((raw): BusinessStoryCandidate => {
      const row = raw && typeof raw === "object" ? raw : {};
      return {
        name: typeof row.name === "string" ? row.name.trim() : "",
        year: typeof row.year === "string" ? row.year.trim() : "",
        problem: typeof row.problem === "string" ? row.problem.trim() : "",
        decisive_move: typeof row.decisive_move === "string" ? row.decisive_move.trim() : "",
        result: typeof row.result === "string" ? row.result.trim() : "",
        visual_potential: typeof row.visual_potential === "string" ? row.visual_potential.trim() : "",
        likely_reframe: typeof row.likely_reframe === "string" ? row.likely_reframe.trim() : "",
        score: parseScore((row as { score?: unknown }).score),
      };
    })
    .filter((candidate) => !!candidate.name && !!candidate.problem && !!candidate.decisive_move);

const rankBusinessStoryCandidates = (candidates: BusinessStoryCandidate[]): BusinessStoryCandidate[] => {
  const candidateStrength = (candidate: BusinessStoryCandidate): number => {
    let bonus = 0;
    if (/\d/.test(candidate.result)) bonus += 0.6;
    if (candidate.problem.length >= 32) bonus += 0.3;
    if (candidate.decisive_move.length >= 24) bonus += 0.4;
    if (candidate.likely_reframe.length >= 24) bonus += 0.4;
    if (candidate.visual_potential.length >= 24) bonus += 0.3;
    return candidate.score + bonus;
  };

  return [...candidates].sort((a, b) => candidateStrength(b) - candidateStrength(a));
};

const normalizeCandidateIdentityPart = (value: string): string =>
  (value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getBusinessStoryCandidateKey = (candidate: BusinessStoryCandidate): string => {
  const nameKey = normalizeCandidateIdentityPart(candidate.name);
  const yearKey = normalizeCandidateIdentityPart(candidate.year);
  const moveKey = normalizeCandidateIdentityPart(candidate.decisive_move)
    .split(" ")
    .filter(Boolean)
    .slice(0, 10)
    .join(" ");

  return yearKey ? `${nameKey}::${yearKey}` : `${nameKey}::${moveKey}`;
};

const dedupeBusinessStoryCandidates = (
  candidates: BusinessStoryCandidate[]
): BusinessStoryCandidate[] => {
  const seen = new Set<string>();
  return candidates.filter((candidate) => {
    const key = getBusinessStoryCandidateKey(candidate);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const buildBusinessStoryCommunityContextBlock = (
  communityContext?: CommunityStoryContext
): string => {
  const base = `COMMUNITY CONTEXT:
Use this context to infer the correct domain, audience, tone, and story world.
Stories must come from the same world as the community and lesson content.`;

  const dynamicLines = [
    communityContext?.title ? `- Community title: ${communityContext.title}` : "",
    communityContext?.categoryName ? `- Category: ${communityContext.categoryName}` : "",
    communityContext?.shortDescription ? `- Short description: ${communityContext.shortDescription}` : "",
    communityContext?.description ? `- Description: ${communityContext.description}` : "",
    communityContext?.tags?.length ? `- Tags: ${communityContext.tags.join(", ")}` : "",
    communityContext?.buyerPersona ? `- Buyer persona: ${communityContext.buyerPersona}` : "",
  ].filter(Boolean);

  return dynamicLines.length
    ? `${base}\n\nAdditional community context:\n${dynamicLines.join("\n")}`
    : base;
};

export const getWeakestBusinessStoryDimension = (
  scores: Record<BusinessStoryScoreKey, number>
): BusinessStoryScoreKey | null => {
  let weakest: BusinessStoryScoreKey | null = null;
  let minValue = Number.POSITIVE_INFINITY;

  BUSINESS_STORY_SCORE_KEYS.forEach((key) => {
    const value = scores[key];
    if (value < minValue) {
      minValue = value;
      weakest = key;
    }
  });

  return weakest;
};

const scoreBusinessStoryRewrite = async (
  scripts: string[],
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides
): Promise<Record<BusinessStoryScoreKey, number> | null> => {
  const scoringPrompt = getThreeStepValidatorPrompt("scoring", threeStepValidatorPromptTemplates);

  console.log("=== STEP 4: SCORING ===");
  console.log("System prompt:\n", scoringPrompt);
  console.log("User message:\n", JSON.stringify(scripts));

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5-mini",
      messages: [
        {
          role: "system",
          content: scoringPrompt,
        },
        {
          role: "user",
          content: JSON.stringify(scripts),
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "{}";
    const normalized = content
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/\s*```$/i, "")
      .trim();
    const parsed = JSON.parse(normalized);
    if (!parsed || typeof parsed !== "object") return null;

    const scores = BUSINESS_STORY_SCORE_KEYS.reduce((acc, key) => {
      acc[key] = parseScore((parsed as Record<string, unknown>)[key]);
      return acc;
    }, {} as Record<BusinessStoryScoreKey, number>);

    return scores;
  } catch {
    return null;
  }
};

export const scoreBusinessStoryScripts = async (
  scripts: string[],
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides
): Promise<Record<BusinessStoryScoreKey, number> | null> =>
  scoreBusinessStoryRewrite(scripts, threeStepValidatorPromptTemplates);

export const generateBusinessStoryCandidates = async (
  text: string,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<BusinessStoryCandidate[]> => {
  const systemPrompt = replacePromptVariables(
    getThreeStepValidatorPrompt("storySelection", threeStepValidatorPromptTemplates),
    { CANDIDATE_COUNT: String(BUSINESS_STORY_CANDIDATE_COUNT) }
  );

  onProgress?.({
    stepId: "story-selection",
    message: "Step 1 of 4: selecting the strongest story...",
    percent: 10,
  });

  console.log("=== STEP 1: STORY SELECTION ===");
  console.log("System prompt:\n", systemPrompt);
  console.log("User message:\n", `Story/lesson context this reel should align with:\n${text}`);

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5.4",
      messages: [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content: `Story/lesson context this reel should align with:\n${text}`,
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const parsed = parseJsonArray(content);
    const normalized = normalizeBusinessStoryCandidates(parsed);
    return dedupeBusinessStoryCandidates(rankBusinessStoryCandidates(normalized)).slice(
      0,
      BUSINESS_STORY_CANDIDATE_COUNT
    );
  } catch (error) {
    console.error("Error generating business story candidates:", error);
    return [];
  }
};

const generateBusinessStoryDraftScripts = async (
  text: string,
  format: VideoFormat,
  reelFormatPromptTemplates: ReelFormatPromptTemplateOverrides | undefined,
  chosenStory: BusinessStoryCandidate,
  writingStyle: string,
  targetMaxWords: number,
  hardMaxWords: number,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<string[] | null> => {
  const styleNote = writingStyle?.trim()
    ? `\nCreator writing style/tone to preserve: ${writingStyle.trim()}`
    : "";
  const communityReelTypePrompt = stripImagePromptRulesFromArc(
    getReelFormatPrompt(format, reelFormatPromptTemplates)
      .replace(/\{\{SEGMENT_COUNT\}\}/g, String(BUSINESS_STORY_SEGMENT_COUNT))
      .replace(/\{\{COMMUNITY_GROUNDING_LINE\}\}/g, "")
      .replace(/\{\{CHARACTER_IDENTITY\}\}/g, "")
  );

  const systemPrompt = replacePromptVariables(
    getThreeStepValidatorPrompt("scriptGeneration", threeStepValidatorPromptTemplates),
    {
      REEL_TYPE_STRUCTURE_GUIDANCE:
        communityReelTypePrompt || "No custom reel-type prompt provided.",
    }
  );

  onProgress?.({
    stepId: "script-generation",
    message: "Step 2 of 4: drafting the reel script...",
    percent: 24,
  });

  console.log("=== STEP 2: SCRIPT GENERATION ===");
  console.log("System prompt:\n", systemPrompt);
  console.log("User message:\n", `Chosen story candidate:\n${JSON.stringify(chosenStory, null, 2)}`);

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5.4",
      messages: [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content: `Chosen story candidate:
${JSON.stringify(chosenStory, null, 2)}

Draft requirements:
- Keep the same core facts from the chosen story
- Do not invent new facts or numbers
- Keep each segment spoken-friendly and concise
- Usually 6-${targetMaxWords} words per segment, but shorter or longer is allowed when needed
- Hard cap ${hardMaxWords} words per segment
- Each segment should feel like the next beat of the same unfolding story${styleNote}
- Return ONLY valid JSON array of exactly ${BUSINESS_STORY_SEGMENT_COUNT} strings.`,
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const parsed = parseJsonArray(content);
    return normalizeGeneratedScripts(parsed, buildFallbackScripts(text));
  } catch (error) {
    console.error("Error generating business story draft:", error);
    return null;
  }
};

export const rewriteBusinessStoryScripts = async (
  scripts: string[],
  chosenStory: BusinessStoryCandidate | null,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides,
  extraFocus?: string,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<string[]> => {
  const rewriteSystemPrompt = replacePromptVariables(
    getThreeStepValidatorPrompt("rewrite", threeStepValidatorPromptTemplates),
    { SEGMENT_COUNT: String(scripts.length) }
  );

  onProgress?.({
    stepId: "rewrite",
    message: extraFocus
      ? "Refreshing the script after your rewrite request..."
      : "Step 3 of 6: tightening the script...",
    percent: extraFocus ? 35 : 40,
  });

  console.log("=== STEP 3: REWRITE ===");
  console.log("System prompt:\n", rewriteSystemPrompt);
  console.log("User message:\n", `Chosen story facts:\n${JSON.stringify(chosenStory, null, 2)}\n\n${extraFocus ? `Additional focus for this pass: ${extraFocus}\n\n` : ""}Here is the script to rewrite:\n${JSON.stringify(scripts)}`);

  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5.4",
      messages: [
        { role: "system", content: rewriteSystemPrompt },
        {
          role: "user",
          content: `${chosenStory ? `Chosen story facts:\n${JSON.stringify(chosenStory, null, 2)}\n\n` : ""}${extraFocus ? `Additional focus for this pass: ${extraFocus}\n\n` : ""}Here is the script to rewrite:
${JSON.stringify(scripts)}`,
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const parsed = parseJsonArray(content);
    return normalizeGeneratedScripts(parsed, scripts);
  } catch (error) {
    console.error("Error rewriting business story scripts:", error);
    return scripts;
  }
};

export const generateBusinessStoryScriptsFromCandidate = async (
  text: string,
  format: VideoFormat,
  chosenStory: BusinessStoryCandidate,
  writingStyle: string,
  reelFormatPromptTemplates?: ReelFormatPromptTemplateOverrides,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<{ scripts: string[]; scores: Record<BusinessStoryScoreKey, number> | null } | null> => {
  const { targetMaxWords, hardMaxWords } = getScriptWordGuidance(format);

  const draftScripts = await generateBusinessStoryDraftScripts(
    text,
    format,
    reelFormatPromptTemplates,
    chosenStory,
    writingStyle,
    targetMaxWords,
    hardMaxWords,
    threeStepValidatorPromptTemplates,
    onProgress
  );
  if (!draftScripts) return null;

  onProgress?.({
    stepId: "scoring",
    message: "Step 3 of 4: scoring the draft...",
    percent: 40,
  });
  const qualityScores = await scoreBusinessStoryRewrite(draftScripts, threeStepValidatorPromptTemplates);

  return { scripts: draftScripts, scores: qualityScores };
};

const generateBusinessStoryScriptsWithPipeline = async (
  text: string,
  format: VideoFormat,
  reelFormatPromptTemplates: ReelFormatPromptTemplateOverrides | undefined,
  threeStepValidatorPromptTemplates: ThreeStepValidatorPromptTemplateOverrides | undefined,
  writingStyle: string,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<{ scripts: string[]; scores: Record<BusinessStoryScoreKey, number> | null } | null> => {
  const candidates = await generateBusinessStoryCandidates(
    text,
    threeStepValidatorPromptTemplates,
    onProgress
  );
  const chosenStory = candidates[0];
  if (!chosenStory) return null;

  return generateBusinessStoryScriptsFromCandidate(
    text,
    format,
    chosenStory,
    writingStyle,
    reelFormatPromptTemplates,
    threeStepValidatorPromptTemplates,
    onProgress
  );
};

export const inferCharacterDescription = async (scripts: string[]): Promise<string> => {
  const joined = scripts.filter(Boolean).join("\n");
  if (!joined.trim()) return "";
  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-4o-mini",
      messages: [
        {
          role: "system",
          content: `You identify the recurring main character or protagonist from story scripts for consistent image generation.

Write ONE sentence (max 35 words) that can be used as a CHARACTER_ANCHOR.

Rules:
- If the script names a real person, use that person as the anchor subject.
- Include the person's name, role/context, approximate age era if inferable, gender presentation if inferable, and a stable wardrobe/setting cue that fits the story world.
- Do not use the voice/narrator/author as the character unless they are explicitly the protagonist of the story.
- Do not return an empty string when a named human protagonist is present.
- If there is no specific human protagonist, return an empty string.

Return ONLY the descriptor sentence, no quotes, no explanation.`,
        },
        { role: "user", content: joined },
      ],
    });
    return (data?.choices?.[0]?.message?.content || "").trim().replace(/^["']|["']$/g, "");
  } catch {
    return "";
  }
};

export const enforceCharacterConsistency = (
  segments: { script: string; imagePrompt: string }[],
  preferredAnchor?: string
): { script: string; imagePrompt: string }[] => {
  const anchor =
    (preferredAnchor || "").trim() ||
    segments
      .map((seg) => extractCharacterAnchor(seg.imagePrompt))
      .find((value): value is string => !!value && value.trim().length > 0) ||
    null;

  // If no anchor is resolved, leave all segments as-is
  if (!anchor) return segments;

  return segments.map((seg) => {
    // Only apply the anchor to segments that already feature the character
    const hasCharacter = /CHARACTER_ANCHOR:/i.test(seg.imagePrompt);
    if (!hasCharacter) return seg;
    return { ...seg, imagePrompt: applyCharacterAnchor(seg.imagePrompt, anchor) };
  });
};

const enforceVisualConsistency = (
  segments: { script: string; imagePrompt: string }[],
  preferredVisualStyle?: string
): { script: string; imagePrompt: string }[] =>
  segments.map((seg) => ({
    ...seg,
    imagePrompt: applyVisualStyleAnchor(seg.imagePrompt, preferredVisualStyle),
  }));

export const generateReelSegments = async (
  text: string,
  format: VideoFormat,
  writingStyle: string,
  communityContext?: CommunityStoryContext,
  reelFormatPromptTemplates?: ReelFormatPromptTemplateOverrides,
  useThreeStepValidator: boolean = true,
  threeStepValidatorPromptTemplates?: ThreeStepValidatorPromptTemplateOverrides,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<{ segments: { script: string; imagePrompt: string }[]; qualityScores?: Record<string, number> }> => {
  const { targetMaxWords, hardMaxWords } = getScriptWordGuidance(format);
  const styleNote = writingStyle?.trim() ? `\nCreator writing style/tone: ${writingStyle.trim()}` : "";
  const communityContextNote = buildCommunityContextNote(communityContext);
  const fallbackScripts = buildFallbackScripts(text);
  let scripts = fallbackScripts;
  let scriptsResolved = false;
  let qualityScores: Record<string, number> | undefined;

  if (useThreeStepValidator && supportsThreeStepValidator(format)) {
    const result = await generateBusinessStoryScriptsWithPipeline(
      text,
      format,
      reelFormatPromptTemplates,
      threeStepValidatorPromptTemplates,
      writingStyle,
      onProgress
    );
    if (result?.scripts?.length) {
      scripts = result.scripts;
      qualityScores = result.scores ?? undefined;
      scriptsResolved = true;
    }
  }

  if (!scriptsResolved) {
    const scriptSystemPrompt = buildReelScriptSystemPrompt(
      format,
      styleNote,
      communityContextNote,
      targetMaxWords,
      hardMaxWords,
      undefined,
      reelFormatPromptTemplates
    );
    const extraHookGuard = `\n\nCritical hook guard:
- Make segment 1 the strongest, clearest, most scroll-stopping line in the reel.
- Build curiosity immediately, but make the premise understandable on first listen.
- Avoid wasting the opening on soft setup or generic framing.${
      format === "historic-arc"
        ? "\n- Use one specific real story, not a generic archetype.\n- Reveal the real protagonist, company, institution, or event within the first 1-2 segments.\n- If segment 1 already names the protagonist or company, do not spend segment 2 re-explaining who it is; escalate instead."
        : ""
    }`;

    try {
      onProgress?.({
        stepId: "script-generation",
        message: "Step 1 of 2: generating the reel script...",
        percent: 14,
      });
      const data = await createOpenAIChatCompletion<any>({
        model: "gpt-5.4",
        messages: [
          { role: "system", content: scriptSystemPrompt },
          {
            role: "user",
            content: format === "historic-arc"
              ? `Create a 60-second viral reel (${SEGMENT_COUNT} segments, roughly ${NARRATION_MIN_SECONDS}-${NARRATION_MAX_SECONDS}s narration per segment) using a real historical or well-known story that illustrates this core principle.
Narration guidance: segments can be shorter or longer as needed, but keep each line concise, natural, and generally under ${hardMaxWords} words.
Do NOT adapt or retell the lesson content below as-is. Extract only the core principle from it, then find the best real story that dramatizes that principle.

${communityContextNote ? `${communityContextNote}\n\n` : ""}Core principle to illustrate (find a real story for this):
${text}${extraHookGuard}`
              : `Create a 60-second viral reel (${SEGMENT_COUNT} segments, roughly ${NARRATION_MIN_SECONDS}-${NARRATION_MAX_SECONDS}s narration per segment) from this lesson content.
Narration guidance: segments can be shorter or longer as needed, but keep each line concise, natural, and generally under ${hardMaxWords} words.

${communityContextNote ? `${communityContextNote}\n\n` : ""}Lesson content:
${text}${extraHookGuard}`,
          },
        ],
      });
      const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
      const parsed = parseJsonArray(content);
      scripts = normalizeGeneratedScripts(parsed, fallbackScripts);
    } catch (error) {
      console.error("Error generating reel scripts:", error);
    }
  }

  const segments = scripts.map((script) => ({ script, imagePrompt: "" }));

  return { segments, qualityScores };
};

export const generateImagePromptsForImportedScripts = async (
  scripts: string[],
  characterDescription: string,
  visualStyle: string,
  communityContext?: CommunityStoryContext,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<string[]> => {
  const characterAnchorNote = characterDescription?.trim()
    ? `When the main character appears in the scene, start the imagePrompt with "CHARACTER_ANCHOR: ${characterDescription.trim()}." Do not add CHARACTER_ANCHOR to abstract or environment-only scenes.`
    : "";
  const visualStyleSentence = `${VISUAL_STYLE_PREFIX} ${normalizeVisualStyleAnchor(visualStyle)}`;
  const communityContextNote = buildCommunityContextNote(communityContext);

  const systemPrompt = `You write cohesive image generation prompts for a 9:16 portrait social media reel. You receive the full script, so treat the prompts as a continuous visual sequence from the same story world, not isolated stock images.

Rules:
- Each output item is one self-contained image prompt for that segment, but it must feel like it belongs in the same reel as the surrounding segments.
- No text, lettering, labels, signage, captions, numbers, logos, watermarks, or readable marks in the image.
- Do NOT include any "Shared visual style:" sentence — the visual style is added automatically
- Do NOT repeat camera/style boilerplate like "9:16 portrait", "cinematic warm film photo", "shallow depth of field", "soft natural light", or color palette notes. Those are handled by the shared visual style.
- ${characterAnchorNote || "If a person appears, describe them fully in the prompt."} Do not force the main character into every prompt.
- Focus on the specific narrative beat, emotion, and human stakes in the script line, while preserving continuity with the whole reel.
- Prefer human-context scenes over empty interiors, product-detail shots, diagrams, or generic b-roll. The humans can be the protagonist, customers, staff, audience members, or affected people depending on the segment.
- Use the protagonist/recurring character only when that person is directly relevant to the spoken beat.
- Use environment-only or object-detail shots sparingly, only when the spoken line is specifically about the environment/object and the shot still carries the story tension.
- For business/history stories, make images feel like moments from the story: founders making decisions, customers reacting, spaces being used, social friction, momentum, pressure, proof. Avoid sterile catalog-like shots.
- Prompts should usually be 1-2 compact sentences. Name the key subject/action/place; do not over-specify props or micro-details that distract from the reel tone.
${communityContextNote ? `\n${communityContextNote}` : ""}

Additional consistency requirements:
${CHARACTER_CONSISTENCY_RULES}

${VISUAL_CONSISTENCY_RULES}

Return ONLY a JSON array of strings, one imagePrompt per script line. Example: ["imagePrompt 1", "imagePrompt 2", ...]`;

  try {
    onProgress?.({
      stepId: "image-prompts",
      message: "Generating image prompts for each segment...",
      percent: 70,
    });
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5-mini",
      messages: [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content: scripts.map((s, i) => `Segment ${i + 1}: "${s}"`).join("\n"),
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const parsed = parseJsonArray(content);

    return enforceVisualConsistency(
      enforceCharacterConsistency(
        scripts.map((script, i) => ({
          script,
          imagePrompt:
            typeof parsed[i] === "string" && parsed[i].trim()
              ? parsed[i].trim()
              : `${visualStyleSentence}. Cinematic 9:16 portrait scene representing: ${script}. Realistic lighting, no text.`,
        })),
        characterDescription
      ),
      visualStyle
    ).map((seg) => seg.imagePrompt);
  } catch (error) {
    console.error("Error generating image prompts for imported scripts:", error);
    const visual = `${visualStyleSentence}`;
    return scripts.map(
      (script) => `${visual}. Cinematic 9:16 portrait scene representing: ${script}. No text in image.`
    );
  }
};

export const buildCharacterReferencePrompt = (
  characterDescription: string,
  visualStyle: string,
  communityContext?: CommunityStoryContext
): string => {
  const domain = [
    communityContext?.categoryName,
    communityContext?.title,
  ].filter(Boolean).join(", ") || "their domain";

  const styleAnchor = normalizeVisualStyleAnchor(visualStyle);

  return `${VISUAL_STYLE_PREFIX} ${styleAnchor}. Cinematic 9:16 portrait establishing shot of ${characterDescription} in their signature environment — a remarkable, memorable setting native to ${domain}. The character is the clear subject: confident posture, facing the lens, expressive but composed. The background environment must be specific, atmospheric, and visually iconic — not generic. Warm directional lighting that flatters the subject. No text, no watermarks, no logos in the image.`;
};

export const generateGeminiImage = async (
  geminiApiKey: string,
  prompt: string,
  referenceImages?: string[],
  refInstruction?: string
): Promise<string> => {
  const parts: any[] = [];

  if (referenceImages && referenceImages.length > 0) {
    for (const refImg of referenceImages) {
      const base64Match = refImg.match(/^data:image\/(\w+);base64,(.+)$/);
      if (base64Match) {
        parts.push({ inline_data: { mime_type: `image/${base64Match[1]}`, data: base64Match[2] } });
      }
    }
    const refNote = refInstruction || "Use the reference images for visual style and mood inspiration only.";
    parts.push({
      text: `${refNote} Create a new image: ${UGC_STYLE_PREFIX}${prompt}. ${PORTRAIT_HINT} IMPORTANT: No text, letters, words, numbers, typography, watermarks, or writing of any kind. Pure visual only.`,
    });
  } else {
    parts.push({
      text: `${UGC_STYLE_PREFIX}${prompt}. ${PORTRAIT_HINT} IMPORTANT: No text, letters, words, numbers, typography, watermarks, or writing of any kind. Pure visual only.`,
    });
  }

  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timeoutId = window.setTimeout(() => controller?.abort(), IMAGE_GENERATION_TIMEOUT_MS);

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-image:generateContent?key=${geminiApiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts }],
          generationConfig: {
            responseModalities: ["TEXT", "IMAGE"],
            imageConfig: { aspectRatio: "9:16" },
          },
        }),
        signal: controller?.signal,
      }
    );

    if (!response.ok) {
      const status = response.status;
      const retryAfter = response.headers.get("retry-after");
      let bodyText = "";
      try {
        bodyText = await response.text();
      } catch {
        bodyText = "<unable to read response body>";
      }
      console.error("[Gemini] image API error", { status, retryAfter, body: bodyText });
      const retryNote = retryAfter ? ` (retry-after: ${retryAfter}s)` : "";
      throw new Error(`Gemini API ${status}${retryNote}: ${bodyText}`);
    }

    const data = await response.json();
    for (const part of data?.candidates?.[0]?.content?.parts || []) {
      const inlineData = part.inline_data || part.inlineData;
      if (inlineData) {
        const mimeType = inlineData.mime_type || inlineData.mimeType;
        return `data:${mimeType};base64,${inlineData.data}`;
      }
    }
    throw new Error("No image returned from Gemini API");
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw new Error(`Gemini image request timed out after ${Math.round(IMAGE_GENERATION_TIMEOUT_MS / 60000)} minutes.`);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const generateGptImage = async (
  prompt: string,
  referenceImages?: string[],
  refInstruction?: string
): Promise<string> => {
  const refs = (referenceImages || []).filter(Boolean);
  const promptBody = refs.length && refInstruction ? `${refInstruction} ${prompt}` : prompt;
  const noTextPrompt = `${UGC_STYLE_PREFIX}${promptBody} ${PORTRAIT_HINT} IMPORTANT: No text, letters, words, numbers, typography, watermarks, or writing of any kind. Pure visual only.`;
  const endpoint = refs.length ? "/images/edits" : "/images/generations";

  let data: any;
  try {
    data = refs.length
      ? await createOpenAIImageEdit<any>({
          model: "gpt-image-2",
          prompt: noTextPrompt,
          images: refs,
          size: GPT_PORTRAIT_SIZE,
          quality: "medium",
          n: 1,
          output_format: "png",
        }, IMAGE_GENERATION_TIMEOUT_MS)
      : await createOpenAIImageGeneration<any>({
          model: "gpt-image-2",
          prompt: noTextPrompt,
          n: 1,
          size: GPT_PORTRAIT_SIZE,
          quality: "medium",
          output_format: "png",
        }, IMAGE_GENERATION_TIMEOUT_MS);
  } catch (err) {
    const e = err as { code?: string; message?: string; details?: { status?: number; response?: any } };
    const status = e?.details?.status;
    const apiMessage = e?.details?.response?.error?.message || e?.message || "Unknown error";
    console.error("[GPT Image] API error", {
      endpoint,
      status,
      code: e?.code,
      message: apiMessage,
      details: e?.details,
    });
    const statusNote = status ? ` ${status}` : "";
    throw new Error(`OpenAI ${endpoint}${statusNote}: ${String(apiMessage)}`);
  }

  const b64 = data?.data?.[0]?.b64_json;
  if (!b64) throw new Error("No image returned from GPT Image API");
  return `data:image/png;base64,${b64}`;
};

// ─── ElevenLabs Voice ─────────────────────────────────────────────────────────

export const prepareNarrationText = (scripts: string[]): string =>
  scripts
    .filter(Boolean)
    .map((script) => {
      let s = script.trim();
      // Ensure sentence ends with punctuation so ElevenLabs doesn't run sentences together
      if (s && !/[.!?…]$/.test(s)) s = s + ".";
      // Convert plain dashes used as pauses into em-dashes (longer pause cue)
      s = s.replace(/\s+-\s+/g, " — ");
      // Add a brief breath pause after question marks mid-sentence
      s = s.replace(/\?(\s+[a-z])/g, "?... $1");
      return s;
    })
    // Two newlines = paragraph break = natural beat/pause between segments
    .join("\n\n");

export type ElevenLabsVoiceSettingsOverride = {
  stability?: number;
  similarityBoost?: number;
  style?: number;
  useSpeakerBoost?: boolean;
};

export const generateElevenLabsVoice = async (
  elevenLabsApiKey: string,
  voiceId: string,
  text: string,
  voiceSettings?: ElevenLabsVoiceSettingsOverride
): Promise<Blob> => {
  const controller = typeof AbortController === "undefined" ? null : new AbortController();
  const timeoutId = window.setTimeout(() => controller?.abort(), 90000);

  try {
    const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
      method: "POST",
      headers: {
        "xi-api-key": elevenLabsApiKey,
        "Content-Type": "application/json",
        Accept: "audio/mpeg",
      },
      body: JSON.stringify({
        text,
        model_id: "eleven_multilingual_v2",
        voice_settings: {
          stability: voiceSettings?.stability ?? 0.35,
          similarity_boost: voiceSettings?.similarityBoost ?? 0.75,
          style: voiceSettings?.style ?? 0.45,
          use_speaker_boost: voiceSettings?.useSpeakerBoost ?? true,
        },
      }),
      signal: controller?.signal,
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`ElevenLabs API error: ${response.status} ${err}`);
    }

    return response.blob();
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw new Error("ElevenLabs request timed out after 90 seconds.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

// ─── Seedance Transitions ─────────────────────────────────────────────────────

const SEEDANCE_BASE_URL = "https://ark.ap-southeast.bytepluses.com/api/v3/contents/generations/tasks";
const SEEDANCE_MODEL = "seedance-1-5-pro-251215";
type SeedanceTransitionOptions = {
  durationSeconds?: 5 | 10;
  prompt?: string;
  pollIntervalMs?: number;
  timeoutMs?: number;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const parseSeedanceTaskId = (payload: any): string | null =>
  payload?.id || payload?.data?.id || payload?.task_id || null;

const parseSeedanceVideoUrl = (payload: any): string | null => {
  const content = payload?.content || payload?.data?.content;
  // Object format: { video_url: "https://..." }
  if (content && typeof content === "object" && !Array.isArray(content)) {
    if (typeof content.video_url === "string") return content.video_url;
    if (typeof content.url === "string") return content.url;
  }
  // Array format: [{ type: "video_url", video_url: { url: "..." } }]
  if (Array.isArray(content)) {
    for (let i = 0; i < content.length; i++) {
      const part = content[i];
      if (part?.type === "video_url" && part?.video_url?.url) return part.video_url.url;
      if (part?.type === "video" && part?.url) return part.url;
    }
  }
  return payload?.data?.video_url || payload?.video_url || payload?.url || null;
};

const parseSeedanceStatus = (payload: any): string =>
  (payload?.status || payload?.data?.status || "").toString().toLowerCase();

export const generateSeedanceTransitionVideo = async (
  seedanceApiKey: string,
  startFrameImage: string,
  endFrameImage?: string,
  options?: SeedanceTransitionOptions
): Promise<string> => {
  const duration = options?.durationSeconds || 5;
  const pollIntervalMs = options?.pollIntervalMs || 4000;
  const timeoutMs = options?.timeoutMs || 300000;
  const prompt =
    (options?.prompt || "Smooth cinematic motion. Keep the same character identity and visual style. No text in frame.") +
    ` --duration ${duration} --camerafixed false --resolution 720p --aspect_ratio 9:16`;

  console.log("[Seedance] starting job, imageUrl length:", startFrameImage?.length, "starts with:", startFrameImage?.slice(0, 30));
  const createResp = await fetch(SEEDANCE_BASE_URL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${seedanceApiKey}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      model: SEEDANCE_MODEL,
      generate_audio: false,
      content: [
        { type: "text", text: prompt },
        { type: "image_url", image_url: { url: startFrameImage }, role: "first_frame" },
        ...(endFrameImage ? [{ type: "image_url", image_url: { url: endFrameImage }, role: "last_frame" }] : []),
      ],
    }),
  });

  if (!createResp.ok) {
    const err = await createResp.text();
    throw new Error(`Seedance create job failed: ${createResp.status} ${err}`);
  }

  const createData = await createResp.json();
  console.log("[Seedance] create response:", JSON.stringify(createData));
  const taskId = parseSeedanceTaskId(createData);
  if (!taskId) {
    throw new Error("Seedance create job did not return task id");
  }
  console.log("[Seedance] task id:", taskId);

  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    await sleep(pollIntervalMs);

    const statusResp = await fetch(`${SEEDANCE_BASE_URL}/${taskId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${seedanceApiKey}`,
        "Content-Type": "application/json",
      },
    });

    if (!statusResp.ok) {
      const err = await statusResp.text();
      throw new Error(`Seedance status request failed: ${statusResp.status} ${err}`);
    }

    const statusData = await statusResp.json();
    console.log("[Seedance] poll response:", JSON.stringify(statusData));
    const status = parseSeedanceStatus(statusData);
    const videoUrl = parseSeedanceVideoUrl(statusData);

    if (videoUrl) return videoUrl;
    if (["failed", "error", "cancelled", "canceled"].includes(status)) {
      throw new Error(`Seedance job failed with status: ${status || "unknown"}`);
    }
  }

  throw new Error("Seedance job timed out");
};

// ─── Multi-character full-dialogue mode ─────────────────────────────────────
//
// Characters come from the community cast library (Community/{id}/ReelCharacters,
// managed in the Media Studio Characters tab and over MCP). In dialogue mode
// there is no narrator: every segment is one spoken line from one cast member,
// rendered later with that character's own ElevenLabs voice.

export type ReelCastMember = {
  characterId: string;
  name: string;
  visualDescription: string;
  personality?: string;
  backstory?: string;
  speakingStyle?: string;
  voiceId?: string;
  referenceImageUrl?: string;
};

export type DialogueSegmentDraft = {
  script: string;
  speakerCharacterId: string;
  speakerName: string;
  characterIds: string[];
};

export const buildCastPromptBlock = (cast: ReelCastMember[]): string =>
  [
    "CAST (the only people who may speak or appear):",
    ...cast.map((c, i) => {
      const bits = [
        `${i + 1}. ${c.name}`,
        c.visualDescription ? `look: ${c.visualDescription}` : "",
        c.personality ? `personality: ${c.personality}` : "",
        c.speakingStyle ? `speaking style: ${c.speakingStyle}` : "",
      ].filter(Boolean);
      return bits.join(" | ");
    }),
  ].join("\n");

const normalizeName = (name: string): string => name.trim().toLowerCase();

export const matchCastMemberByName = (
  name: string,
  cast: ReelCastMember[]
): ReelCastMember | null => {
  const target = normalizeName(name || "");
  if (!target) return null;
  return (
    cast.find((c) => normalizeName(c.name) === target) ||
    cast.find(
      (c) =>
        normalizeName(c.name).startsWith(target) ||
        target.startsWith(normalizeName(c.name)) ||
        normalizeName(c.name).split(/\s+/)[0] === target.split(/\s+/)[0]
    ) ||
    null
  );
};

// Deterministically prepend the anchors of the characters present in a scene.
// Multi-character version of applyCharacterAnchor: one CHARACTER_ANCHOR block
// per present character, in cast order, so Gemini keeps every face consistent.
export const applyCastAnchors = (
  prompt: string,
  cast: ReelCastMember[],
  characterIds: string[]
): string => {
  const base = prompt
    .replace(/CHARACTER_ANCHOR\s*(?:\([^)]*\))?:[^.]*\.\s*/g, "")
    .trim();
  const present = cast.filter((c) => characterIds.includes(c.characterId));
  if (!present.length) return base;
  const anchors = present
    .map((c) => `CHARACTER_ANCHOR (${c.name}): ${c.visualDescription.trim().replace(/\.$/, "")}.`)
    .join(" ");
  return `${anchors} ${base}`;
};

export const generateDialogueReelSegments = async (
  text: string,
  format: VideoFormat,
  writingStyle: string,
  cast: ReelCastMember[],
  communityContext?: CommunityStoryContext,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<{ segments: DialogueSegmentDraft[] }> => {
  const { hardMaxWords } = getScriptWordGuidance(format);
  const styleNote = writingStyle?.trim() ? `\nCreator writing style/tone: ${writingStyle.trim()}` : "";
  const communityContextNote = buildCommunityContextNote(communityContext);
  const castBlock = buildCastPromptBlock(cast);

  const systemPrompt = `You write mini movies for 9:16 viral reels told ENTIRELY through character dialogue. There is NO narrator and NO voiceover. Every segment is exactly one spoken line delivered by exactly one cast member, in character.

${castBlock}

Rules:
- Exactly ${SEGMENT_COUNT} segments. One speaker per segment.
- Every line must sound like natural speech from that character: their personality, their speaking style, their stakes. No exposition dumps.
- The story must still work as a viral reel: segment 1 is a scroll-stopping hook line, tension escalates, and the final segments land a payoff or reframe.
- Alternate speakers to create real back-and-forth. Do not give one character more than 2 consecutive lines.
- Use only the cast above. Never invent new speaking characters.
- Keep each line under ${hardMaxWords} words, natural and punchy.
- "present" lists the cast members visible in the shot for that segment (the speaker is always present; add listeners only when the shot needs them).${styleNote}

Return ONLY a JSON array of ${SEGMENT_COUNT} objects: [{"speaker": "<cast name>", "line": "<spoken line>", "present": ["<cast name>", ...]}, ...]`;

  onProgress?.({
    stepId: "script-generation",
    message: "Writing the dialogue between your characters...",
    percent: 14,
  });

  const data = await createOpenAIChatCompletion<any>({
    model: "gpt-5.4",
    messages: [
      { role: "system", content: systemPrompt },
      {
        role: "user",
        content: `Create the ${SEGMENT_COUNT}-segment dialogue mini movie from this source material. Extract the core idea and dramatize it through the cast's conversation.

${communityContextNote ? `${communityContextNote}\n\n` : ""}Source material:
${text}`,
      },
    ],
  });

  const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
  const parsed = parseJsonArray(content);
  const fallbackSpeaker = cast[0];

  const segments: DialogueSegmentDraft[] = parsed
    .filter((item: any) => item && typeof item === "object" && typeof item.line === "string")
    .slice(0, SEGMENT_COUNT)
    .map((item: any) => {
      const speaker = matchCastMemberByName(String(item.speaker || ""), cast) || fallbackSpeaker;
      const presentNames: string[] = Array.isArray(item.present)
        ? item.present.filter((p: any): p is string => typeof p === "string")
        : [];
      const presentIds = presentNames
        .map((n) => matchCastMemberByName(n, cast)?.characterId)
        .filter((id): id is string => !!id);
      const characterIds = Array.from(new Set([speaker.characterId, ...presentIds]));
      return {
        script: String(item.line).trim(),
        speakerCharacterId: speaker.characterId,
        speakerName: speaker.name,
        characterIds,
      };
    });

  if (!segments.length) {
    throw new Error("Dialogue generation returned no usable segments.");
  }
  return { segments };
};

export const generateDialogueImagePrompts = async (
  segments: DialogueSegmentDraft[],
  cast: ReelCastMember[],
  visualStyle: string,
  communityContext?: CommunityStoryContext,
  onProgress?: (update: ReelGenerationProgressUpdate) => void
): Promise<string[]> => {
  const communityContextNote = buildCommunityContextNote(communityContext);
  const castBlock = buildCastPromptBlock(cast);

  const systemPrompt = `You write cohesive image generation prompts for a 9:16 portrait dialogue mini movie. Every segment is a spoken line from a specific character, so each shot must show the SPEAKER delivering the line (plus any listed listeners), like film coverage of a conversation.

${castBlock}

Rules:
- One prompt per segment. Continuous visual sequence from the same story world.
- Show the speaker mid-line: expression, gesture, body language matching the emotion of the line. Vary framing (close-up, over-the-shoulder, two-shot, reaction cuts) like edited film coverage.
- Do NOT describe the characters' physical appearance. Their identity anchors are added automatically. Refer to them by name only.
- No text, lettering, labels, signage, captions, numbers, logos, watermarks, or readable marks in the image.
- Do NOT repeat camera/style boilerplate like "9:16 portrait" or lighting/palette notes. The shared visual style is added automatically.
- Keep prompts to 1-2 compact sentences: who is in frame, what they are doing, where, and the emotional beat.
${communityContextNote ? `\n${communityContextNote}` : ""}

Return ONLY a JSON array of strings, one imagePrompt per segment.`;

  onProgress?.({
    stepId: "image-prompts",
    message: "Storyboarding each dialogue beat...",
    percent: 70,
  });

  const visualStyleSentence = `${VISUAL_STYLE_PREFIX} ${normalizeVisualStyleAnchor(visualStyle)}`;
  const castById = new Map(cast.map((c) => [c.characterId, c]));

  let prompts: string[];
  try {
    const data = await createOpenAIChatCompletion<any>({
      model: "gpt-5-mini",
      messages: [
        { role: "system", content: systemPrompt },
        {
          role: "user",
          content: segments
            .map((seg, i) => {
              const others = seg.characterIds
                .filter((id) => id !== seg.speakerCharacterId)
                .map((id) => castById.get(id)?.name)
                .filter(Boolean);
              return `Segment ${i + 1}: ${seg.speakerName} says "${seg.script}"${
                others.length ? ` (also in frame: ${others.join(", ")})` : ""
              }`;
            })
            .join("\n"),
        },
      ],
    });
    const content = data?.choices?.[0]?.message?.content?.trim() || "[]";
    const parsed = parseJsonArray(content);
    prompts = segments.map((seg, i) =>
      typeof parsed[i] === "string" && parsed[i].trim()
        ? parsed[i].trim()
        : `${seg.speakerName} speaking with intensity, mid-conversation, cinematic scene matching: ${seg.script}`
    );
  } catch (error) {
    console.error("Error generating dialogue image prompts:", error);
    prompts = segments.map(
      (seg) => `${seg.speakerName} speaking with intensity, mid-conversation, cinematic scene matching: ${seg.script}`
    );
  }

  return prompts.map((prompt, i) => {
    const withAnchors = applyCastAnchors(prompt, cast, segments[i].characterIds);
    return `${visualStyleSentence}. ${withAnchors}`;
  });
};
